// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

import { clsx } from 'clsx';

/**
 * Utility function to merge class names
 */
export function cn(...inputs: Parameters<typeof clsx>) {
  return clsx(...inputs);
}

/**
 * Format player name for display
 */
export function formatPlayerName(name: string, isYou: boolean = false): string {
  return isYou ? `${name} (You)` : name;
}

/**
 * Get status display text
 */
export function getStatusDisplayText(status: string | Record<string, unknown>): string {
  // Handle objects that should be strings (defensive check)
  if (typeof status === 'object' && status !== null) {
    return 'In Progress';
  }

  if (typeof status !== 'string') {
    return 'In Progress';
  }

  // Only common/universal phases are mapped here
  // Game-specific phases use the generic formatter
  const statusMap: Record<string, string> = {
    lobby: 'Waiting in Lobby',
    playing: 'In Progress',
    finished: 'Game Over',
    game_over: 'Game Over',
  };

  if (statusMap[status]) {
    return statusMap[status];
  }

  // Generic formatter: "prompt_display" -> "Prompt Display"
  if (status) {
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  return 'In Progress';
}

/**
 * Truncate text to a maximum length
 */
export function truncateText(text: string, maxLength: number = 100): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

/**
 * Get ordinal suffix for numbers (1st, 2nd, 3rd, etc.)
 */
export function getOrdinalSuffix(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}
